import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import HtmlEditor from "@/components/HtmlEditor";
import { formatLocal } from "@/lib/timezone";
import {
  Plus, FileText, PaperPlaneTilt, Check, X, ClockCounterClockwise, PencilSimple,
  Trash, ArrowUp, ArrowDown, Eye, DownloadSimple, Warning, CheckCircle
} from "@phosphor-icons/react";
import { SafeHtml } from "@/lib/safeHtml";

const STATUS_COLORS = {
  draft:            "bg-[#F5F5F5] text-[#525252]",
  submitted:        "bg-[#FFFBEB] text-[#D97706] border border-[#FDE68A]",
  approved:         "bg-[#ECFDF5] text-[#059669] border border-[#A7F3D0]",
  rejected:         "bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]",
  sent:             "bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]",
  client_returned:  "bg-[#FFF7ED] text-[#EA580C] border border-[#FED7AA]",
  client_approved:  "bg-[#DCFCE7] text-[#047857] border border-[#86EFAC]",
};

const STATUS_LABEL = {
  draft: "Draft",
  submitted: "Awaiting Internal Approval",
  approved: "Approved Internally",
  rejected: "Rejected",
  sent: "Sent to Client · Awaiting Review",
  client_returned: "Returned by Client",
  client_approved: "Approved by Client",
};

// ─── List view (shown on Client page Specs tab) ────────────────────────────
export default function ClientSpecsTab({ clientId, clientName }) {
  const { canSee, canWrite } = useAuth();
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeSpecId, setActiveSpecId] = useState(null);

  const canView  = canSee("spec_writing");
  const canEdit  = canWrite("spec_writing");

  const load = () => {
    setLoading(true);
    api.get(`/specs/tenants/${clientId}`)
      .then(r => setSpecs(r.data || []))
      .catch(() => toast.error("Unable to load specs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (canView) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, canView]);

  if (!canView) {
    return (
      <div className="p-8 text-center text-[#A3A3A3] bg-white border border-[#E5E5E5]" data-testid="specs-no-access">
        <Warning size={28} className="mx-auto mb-3 text-[#D97706]" weight="duotone" />
        <p className="text-sm font-bold text-[#0A0A0A]">You do not have access to Spec Writing.</p>
        <p className="text-xs mt-1">Ask a Platform Admin to grant access on the Team page.</p>
      </div>
    );
  }

  if (activeSpecId) {
    return <SpecEditor specId={activeSpecId} clientName={clientName} onBack={() => { setActiveSpecId(null); load(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="client-specs-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Spec Documents</h3>
          <p className="text-xs text-[#525252] mt-0.5">{specs.length} spec{specs.length !== 1 ? "s" : ""} for {clientName}</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowNew(true)} className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white" data-testid="new-spec-button">
            <Plus size={14} className="mr-1.5" /> New Spec
          </Button>
        )}
      </div>

      {loading ? (
        <div className="h-24 bg-[#F5F5F5] animate-pulse" />
      ) : specs.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E5E5E5] p-10 text-center">
          <FileText size={32} className="text-[#E5E5E5] mx-auto mb-3" />
          <p className="text-sm text-[#A3A3A3]">No specs yet for this client.</p>
          {canEdit && (
            <button onClick={() => setShowNew(true)} className="text-[#0055FF] text-sm hover:underline mt-1" data-testid="new-spec-empty">
              Start the first spec
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E5]">
          {/* Table with key audit dates per spec so the status of each one is visible at a glance. */}
          <table className="w-full text-sm" data-testid="specs-table">
            <thead>
              <tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Spec</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Status</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Sent</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Opened</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Returned</th>
                <th className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Approved</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {specs.map(s => (
                <tr
                  key={s.id}
                  onClick={() => setActiveSpecId(s.id)}
                  className="hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                  data-testid={`spec-row-${s.id}`}
                >
                  <td className="px-3 py-3">
                    <div className="flex items-start gap-2">
                      <FileText size={16} className="text-[#A3A3A3] flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        {s.spec_number && (
                          <p className="text-[10px] font-bold text-[#0055FF] font-mono uppercase tracking-wider" data-testid={`spec-number-${s.id}`}>
                            {s.spec_number}
                          </p>
                        )}
                        <p className="text-sm font-bold text-[#0A0A0A] truncate">{s.title}</p>
                        <p className="text-[10px] text-[#A3A3A3] truncate">
                          {s.created_by_name || "—"} · updated {s.updated_at ? formatLocal(s.updated_at) : "—"}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 inline-block ${STATUS_COLORS[s.status] || "bg-[#F5F5F5] text-[#525252]"}`}>
                      {STATUS_LABEL[s.status] || s.status}
                    </span>
                    {(s.current_version || 1) > 1 && (
                      <span className="ml-1.5 text-[9px] font-black tracking-[1px] px-1.5 py-0.5 inline-block bg-[#EFF6FF] text-[#1D4ED8]" data-testid={`spec-row-version-${s.id}`}>
                        v{s.current_version}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-[#525252] font-mono">{s.sent_at ? formatLocal(s.sent_at) : "—"}</td>
                  <td className="px-3 py-3 text-xs text-[#525252] font-mono">{s.first_opened_at ? formatLocal(s.first_opened_at) : "—"}</td>
                  <td className="px-3 py-3 text-xs text-[#525252] font-mono">{s.client_returned_at ? formatLocal(s.client_returned_at) : "—"}</td>
                  <td className="px-3 py-3 text-xs text-[#525252] font-mono">{s.client_approved_at ? formatLocal(s.client_approved_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <NewSpecDialog
          clientId={clientId}
          onClose={() => setShowNew(false)}
          onCreated={(id) => { setShowNew(false); setActiveSpecId(id); }}
        />
      )}
    </div>
  );
}


// ─── Create new spec dialog ────────────────────────────────────────────────
function NewSpecDialog({ clientId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const r = await api.post("/specs", { tenant_id: clientId, title: title.trim(), summary });
      toast.success("Spec created as draft");
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to create spec");
    }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
        <DialogHeader><DialogTitle className="font-bold">New Specification</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2" data-testid="new-spec-dialog">
          <div>
            <label className="text-[10px] font-bold uppercase text-[#525252]">Title</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} className="rounded-none mt-1" placeholder="e.g. Punchout catalog enhancement" data-testid="new-spec-title" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-[#525252]">Short Summary (optional)</label>
            <textarea value={summary} onChange={e => setSummary(e.target.value)} className="w-full rounded-none mt-1 border border-[#E5E5E5] px-3 py-2 text-sm" rows={3} placeholder="One-paragraph description of what this spec covers…" data-testid="new-spec-summary" />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-none">Cancel</Button>
            <Button onClick={save} disabled={saving} className="flex-1 rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white" data-testid="new-spec-save">
              {saving ? "Creating…" : "Create Draft"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ─── Full spec editor ──────────────────────────────────────────────────────
function SpecEditor({ specId, clientName, onBack }) {
  const { user, hasRole, canWrite } = useAuth();
  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const isPlatformAdmin = hasRole("platform_admin");
  const canEditWriter = canWrite("spec_writing");
  // Editability mirrors backend _require_can_edit:
  //   draft / rejected   → anyone with spec_writing write
  //   submitted          → platform admin only (the "further editing" case)
  //   approved / sent    → locked
  const status = spec?.status;
  const canEditBody = (!status) ? false
    : (status === "submitted" ? isPlatformAdmin
    : (status === "draft" || status === "rejected") ? canEditWriter
    : (status === "client_returned") ? canEditWriter  // iterate based on client feedback
    : false);

  const load = () => {
    setLoading(true);
    api.get(`/specs/${specId}`).then(r => { setSpec(r.data); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [specId]);

  const refresh = r => setSpec(r.data);

  const saveMeta = async (patch) => {
    try { const r = await api.put(`/specs/${specId}`, patch); refresh(r); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const addSection = async () => {
    try {
      const r = await api.post(`/specs/${specId}/sections`, { heading: "Untitled section", body_html: "" });
      refresh(r); toast.success("Section added");
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to add section"); }
  };

  const updateSection = async (sec, patch) => {
    try { const r = await api.put(`/specs/${specId}/sections/${sec.id}`, patch); refresh(r); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deleteSection = async (sec) => {
    if (!window.confirm(`Delete section "${sec.heading}"?`)) return;
    try { const r = await api.delete(`/specs/${specId}/sections/${sec.id}`); refresh(r); toast.success("Section removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Unable to remove"); }
  };

  const moveSection = async (idx, dir) => {
    const next = [...spec.sections];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= next.length) return;
    [next[idx], next[tgt]] = [next[tgt], next[idx]];
    try {
      const r = await api.post(`/specs/${specId}/sections/reorder`, { section_ids: next.map(s => s.id) });
      refresh(r);
    } catch (e) { toast.error(e.response?.data?.detail || "Reorder failed"); }
  };

  const submitForApproval = async () => {
    if (!(spec.sections || []).length) return toast.error("Add at least one section before submitting");
    try {
      const r = await api.post(`/specs/${specId}/submit`);
      refresh(r); toast.success("Submitted for Platform Admin approval");
    } catch (e) { toast.error(e.response?.data?.detail || "Submit failed"); }
  };

  const approve = async () => {
    try { const r = await api.post(`/specs/${specId}/approve`); refresh(r); toast.success("Approved — ready to send"); }
    catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
  };

  const previewPdf = async () => {
    try {
      const r = await api.get(`/specs/${specId}/preview`, { responseType: "text" });
      const blob = new Blob([r.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      // Revoke after a minute so memory is freed but the tab has time to load.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/specs/${specId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Eyvo_Spec_${(spec.title || "spec").replace(/[^a-z0-9\- _]/gi, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  if (loading || !spec) return <div className="p-8 text-center text-[#A3A3A3]">Loading spec…</div>;

  return (
    <div className="space-y-5" data-testid="spec-editor">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={onBack} className="text-xs text-[#A3A3A3] hover:text-[#0055FF] uppercase tracking-wider font-bold" data-testid="spec-back">← Back to specs</button>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {spec.spec_number && (
              <span className="text-[11px] font-bold text-[#0055FF] font-mono uppercase tracking-wider" data-testid="spec-number">
                {spec.spec_number}
              </span>
            )}
            <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 ${STATUS_COLORS[status] || ""}`} data-testid="spec-status">
              {STATUS_LABEL[status] || status}
            </span>
            {(spec.current_version || 1) > 0 && (status === "sent" || status === "client_returned" || status === "client_approved") && (
              <span
                className="text-[10px] font-black tracking-[1.5px] px-2 py-0.5 bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]"
                data-testid="spec-version-badge"
              >
                v{spec.current_version || 1}
              </span>
            )}
            {spec.rejection_reason && status === "rejected" && (
              <span className="text-[11px] text-[#DC2626]">Rejection reason: {spec.rejection_reason}</span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button variant="outline" onClick={previewPdf} className="rounded-none" data-testid="spec-preview"><Eye size={14} className="mr-1.5" /> Preview</Button>
          <Button variant="outline" onClick={downloadPdf} className="rounded-none" data-testid="spec-download"><DownloadSimple size={14} className="mr-1.5" /> Download PDF</Button>
          <Button variant="outline" onClick={() => setShowHistory(true)} className="rounded-none" data-testid="spec-history"><ClockCounterClockwise size={14} className="mr-1.5" /> History</Button>
          {canEditWriter && (status === "draft" || status === "rejected") && (
            <Button onClick={submitForApproval} className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white" data-testid="spec-submit">
              <PaperPlaneTilt size={14} className="mr-1.5" /> Submit for Approval
            </Button>
          )}
          {isPlatformAdmin && status === "submitted" && (
            <>
              <Button onClick={approve} className="rounded-none bg-[#059669] hover:bg-[#047857] text-white" data-testid="spec-approve"><Check size={14} className="mr-1.5" /> Approve</Button>
              <Button onClick={() => setShowReject(true)} variant="outline" className="rounded-none text-[#DC2626] border-[#FECACA] hover:bg-[#FEF2F2]" data-testid="spec-reject"><X size={14} className="mr-1.5" /> Reject</Button>
            </>
          )}
          {isPlatformAdmin && status === "approved" && (
            <Button onClick={() => setShowSend(true)} className="rounded-none bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="spec-send"><PaperPlaneTilt size={14} className="mr-1.5" /> Send to Client</Button>
          )}
          {isPlatformAdmin && status === "client_returned" && (
            <Button onClick={() => setShowSend(true)} className="rounded-none bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="spec-resend">
              <PaperPlaneTilt size={14} className="mr-1.5" /> Send v{(spec.current_version || 1) + 1} to Client
            </Button>
          )}
        </div>
      </div>

      {/* Audit strip — full trail of dates whenever we have anything to show */}
      {(spec.sent_at || spec.approved_at || spec.first_opened_at || spec.client_returned_at || spec.client_approved_at) && (
        <div className="bg-[#FAFAFA] border border-[#E5E5E5] px-4 py-3 text-[11px] text-[#525252] flex flex-wrap gap-x-5 gap-y-1" data-testid="spec-audit-strip">
          {spec.approved_at && <span>✓ Internally approved · {formatLocal(spec.approved_at)}</span>}
          {spec.sent_at && <span>✉ Sent to {spec.sent_to_email} · {formatLocal(spec.sent_at)}</span>}
          {spec.first_opened_at && <span>👁 Opened by client · {formatLocal(spec.first_opened_at)}</span>}
          {spec.client_returned_at && <span className="text-[#D97706] font-bold">↩ Returned with comments · {formatLocal(spec.client_returned_at)}</span>}
          {spec.client_approved_at && <span className="text-[#059669] font-bold">★ Approved by client · {formatLocal(spec.client_approved_at)}</span>}
        </div>
      )}

      {/* Big green APPROVED banner when the client has approved the
          current version. Puts the "ready to move forward" signal
          front-and-centre for the delivery team. */}
      {status === "client_approved" && (
        <div className="bg-[#059669] text-white px-6 py-4 border-4 border-[#047857] flex items-center gap-4" data-testid="spec-approved-banner">
          <CheckCircle size={40} weight="fill" className="flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xl sm:text-2xl font-black uppercase tracking-[2px] leading-none">Client Approved</p>
            <p className="text-xs font-bold mt-1 opacity-90 uppercase tracking-wider">
              <span className="normal-case">v{spec.decided_on_version || spec.current_version}</span>
              {spec.client_approved_in_client_tz?.tz_label ? (
                <>
                  {" · "}
                  <span className="normal-case">{spec.client_approved_in_client_tz.tz_label}</span>
                </>
              ) : spec.client_approved_at ? (
                <>{" · "}{formatLocal(spec.client_approved_at)}</>
              ) : null}
              {" · "}Ready to proceed
            </p>
            {spec.client_approved_in_client_tz?.tz_name && spec.client_name && (
              <p className="text-[10px] font-bold mt-0.5 opacity-80 uppercase tracking-wider" data-testid="spec-approved-tz-note">
                Client local time · {spec.client_name} · <span className="normal-case">{spec.client_approved_in_client_tz.tz_name}</span>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Latest client comment visible to the Eyvo team when the spec was returned */}
      {spec.client_last_comment && status === "client_returned" && (
        <div className="bg-[#FFFBEB] border-l-4 border-[#D97706] px-4 py-3 text-sm text-[#0A0A0A] whitespace-pre-wrap" data-testid="spec-client-comment">
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#D97706] mb-1">Latest client comment</p>
          {spec.client_last_comment}
        </div>
      )}

      {/* Title + Summary */}
      <div className="bg-white border border-[#E5E5E5] p-5 space-y-3">
        <div>
          <label className="text-[10px] font-bold uppercase text-[#525252]">Title</label>
          <Input
            value={spec.title || ""}
            disabled={!canEditBody}
            onChange={e => setSpec({ ...spec, title: e.target.value })}
            onBlur={e => e.target.value !== spec.title && saveMeta({ title: e.target.value })}
            className="rounded-none mt-1 font-bold text-base"
            data-testid="spec-title"
          />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-[#525252]">Summary</label>
          <textarea
            value={spec.summary || ""}
            disabled={!canEditBody}
            onChange={e => setSpec({ ...spec, summary: e.target.value })}
            onBlur={e => e.target.value !== spec.summary && saveMeta({ summary: e.target.value })}
            className="w-full rounded-none mt-1 border border-[#E5E5E5] px-3 py-2 text-sm disabled:bg-[#FAFAFA]"
            rows={3}
            data-testid="spec-summary"
          />
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">Sections</h3>
          {canEditBody && (
            <Button onClick={addSection} variant="outline" className="rounded-none" data-testid="spec-add-section">
              <Plus size={14} className="mr-1.5" /> Add Section
            </Button>
          )}
        </div>
        {(spec.sections || []).length === 0 ? (
          <div className="bg-white border border-dashed border-[#E5E5E5] p-10 text-center text-sm text-[#A3A3A3]">
            No sections yet. {canEditBody && <button onClick={addSection} className="text-[#0055FF] hover:underline">Add the first section</button>}
          </div>
        ) : (
          spec.sections.map((sec, i) => (
            <SectionCard
              key={sec.id}
              idx={i}
              total={spec.sections.length}
              section={sec}
              canEdit={canEditBody}
              onUpdate={(patch) => updateSection(sec, patch)}
              onDelete={() => deleteSection(sec)}
              onMove={(dir) => moveSection(i, dir)}
            />
          ))
        )}
      </div>

      {showHistory && <HistoryDrawer specId={specId} onClose={() => setShowHistory(false)} />}
      {showSend && (
        <SendDialog
          clientId={spec.tenant_id}
          spec={spec}
          onClose={() => setShowSend(false)}
          onSent={(r) => { setShowSend(false); refresh(r); toast.success(spec.status === "client_returned" ? `v${(spec.current_version||1)+1} sent to client` : "Spec sent to client"); }}
          specId={specId}
        />
      )}
      {showReject && (
        <RejectDialog
          onClose={() => setShowReject(false)}
          onDone={(r) => { setShowReject(false); refresh(r); toast.success("Spec rejected — sent back to author"); }}
          specId={specId}
        />
      )}
      {/* Name hint for unused imports */}
      <span className="hidden">{user?.id}{clientName}<PencilSimple size={0} /><Trash size={0} /><CheckCircle size={0} /></span>
    </div>
  );
}


function SectionCard({ idx, total, section, canEdit, onUpdate, onDelete, onMove }) {
  const [heading, setHeading] = useState(section.heading || "");
  const [body, setBody] = useState(section.body_html || "");
  useEffect(() => { setHeading(section.heading || ""); setBody(section.body_html || ""); }, [section.id, section.heading, section.body_html]);
  const [dirty, setDirty] = useState(false);
  const save = () => { if (!dirty) return; onUpdate({ heading, body_html: body }); setDirty(false); };
  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid={`section-card-${section.id}`}>
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[#F5F5F5] bg-[#FAFAFA]">
        <span className="text-[10px] font-bold text-[#A3A3A3] uppercase tracking-wider w-6">#{idx + 1}</span>
        <Input
          value={heading}
          disabled={!canEdit}
          onChange={e => { setHeading(e.target.value); setDirty(true); }}
          onBlur={save}
          className="rounded-none border-0 bg-transparent font-bold text-sm focus-visible:ring-0 px-0"
          placeholder="Section heading"
          data-testid={`section-heading-${section.id}`}
        />
        {canEdit && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => onMove(-1)} disabled={idx === 0} className="p-1 hover:bg-white disabled:opacity-30" title="Move up" data-testid={`section-up-${section.id}`}><ArrowUp size={14} /></button>
            <button onClick={() => onMove(1)} disabled={idx === total - 1} className="p-1 hover:bg-white disabled:opacity-30" title="Move down" data-testid={`section-down-${section.id}`}><ArrowDown size={14} /></button>
            <button onClick={onDelete} className="p-1 hover:bg-[#FEF2F2] text-[#DC2626]" title="Delete section" data-testid={`section-delete-${section.id}`}><Trash size={14} /></button>
          </div>
        )}
      </div>
      <div className="p-0">
        {canEdit ? (
          <HtmlEditor
            value={body}
            onChange={(v) => { setBody(v); setDirty(true); }}
            placeholder="Write this section's specification content…"
            minHeight={220}
            testId={`section-body-${section.id}`}
          />
        ) : (
          // body is the persisted section body_html, sanitised on write
          // (routes/specs.py). SafeHtml re-sanitises at render so legacy
          // rows also pass the allowlist before reaching the DOM.
          <SafeHtml
            className="prose prose-sm max-w-none px-4 py-3 min-h-[120px]"
            html={body || "<p class='text-[#A3A3A3]'>(empty)</p>"}
          />
        )}
        {canEdit && dirty && (
          <div className="px-4 py-2 border-t border-[#F5F5F5] flex justify-end bg-[#FAFAFA]">
            <Button onClick={save} className="rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white h-8 text-xs" data-testid={`section-save-${section.id}`}>Save section</Button>
          </div>
        )}
      </div>
    </div>
  );
}


function HistoryDrawer({ specId, onClose }) {
  const [versions, setVersions] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("iterations");
  useEffect(() => {
    Promise.all([
      api.get(`/specs/${specId}/history`).catch(() => ({ data: [] })),
      api.get(`/specs/${specId}/revisions`).catch(() => ({ data: [] })),
    ]).then(([h, r]) => {
      setVersions(h.data || []);
      setRevisions(r.data || []);
    }).finally(() => setLoading(false));
  }, [specId]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-2xl">
        <DialogHeader><DialogTitle className="font-bold">Specification History</DialogTitle></DialogHeader>
        <div className="flex border-b border-[#E5E5E5] -mx-6 px-6">
          <button
            onClick={() => setTab("iterations")}
            className={`text-[11px] font-bold uppercase tracking-wider px-4 py-2 border-b-2 ${tab === "iterations" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3]"}`}
            data-testid="history-tab-iterations"
          >
            Client iterations ({versions.length})
          </button>
          <button
            onClick={() => setTab("audit")}
            className={`text-[11px] font-bold uppercase tracking-wider px-4 py-2 border-b-2 ${tab === "audit" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3]"}`}
            data-testid="history-tab-audit"
          >
            Full audit trail ({revisions.length})
          </button>
        </div>
        {loading ? <p className="text-sm text-[#A3A3A3] py-4">Loading…</p> : tab === "iterations" ? (
          versions.length === 0 ? (
            <p className="text-sm text-[#A3A3A3] py-4">Not yet sent to the client — no iterations to show.</p>
          ) : (
            <div className="max-h-[60vh] overflow-auto pt-2" data-testid="history-iterations-list">
              {versions.map(v => (
                <div key={v.version} className="py-3 border-b border-[#F5F5F5]">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-black bg-[#0055FF] text-white px-2.5 py-0.5 tracking-wider">v{v.version}</span>
                    <span className="text-[11px] text-[#525252]">Sent by {v.sent_by_name || "—"} · {formatLocal(v.sent_at)}</span>
                    <span className="text-[11px] text-[#A3A3A3]">→ {v.sent_to_email}</span>
                  </div>
                  <ul className="text-xs text-[#525252] space-y-0.5 ml-2">
                    {v.first_opened_at && <li>• Opened by client · {formatLocal(v.first_opened_at)}</li>}
                    {v.client_returned_at && <li className="text-[#D97706] font-bold">• Returned with comments · {formatLocal(v.client_returned_at)}</li>}
                    {v.client_approved_at && <li className="text-[#059669] font-bold">★ Approved · {formatLocal(v.client_approved_at)}</li>}
                    {!v.client_returned_at && !v.client_approved_at && <li className="text-[#A3A3A3]">• Awaiting client decision</li>}
                  </ul>
                  {v.admin_message && (
                    <div className="mt-2 ml-2 text-[11px] italic text-[#525252]">Eyvo note: "{v.admin_message}"</div>
                  )}
                  {v.client_comment && (
                    <div className="mt-2 ml-2 p-2.5 bg-[#FFFBEB] border-l-4 border-[#D97706] text-xs text-[#0A0A0A] whitespace-pre-wrap">
                      <p className="text-[9px] font-bold uppercase tracking-wider text-[#D97706] mb-1">Client comment on v{v.version}</p>
                      {v.client_comment}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          revisions.length === 0 ? (
            <p className="text-sm text-[#A3A3A3] py-4">No revisions yet.</p>
          ) : (
            <div className="divide-y divide-[#F5F5F5] max-h-[60vh] overflow-auto" data-testid="spec-history-list">
              {revisions.map(r => (
                <div key={r.id} className="py-2.5 flex items-start gap-3 text-xs">
                  <span className="w-10 text-[#A3A3A3] font-mono">#{r.version}</span>
                  <div className="flex-1">
                    <p className="font-bold text-[#0A0A0A]">{r.action.replace(/_/g, " ")}</p>
                    {r.note && <p className="text-[#525252] italic mt-0.5">"{r.note}"</p>}
                    <p className="text-[10px] text-[#A3A3A3] mt-0.5">{r.changed_by_name || "—"} · {formatLocal(r.changed_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}


function RejectDialog({ specId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Please provide a reason");
    setSaving(true);
    try { const r = await api.post(`/specs/${specId}/reject`, { reason }); onDone(r); }
    catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
        <DialogHeader><DialogTitle className="font-bold">Reject Specification</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2" data-testid="reject-dialog">
          <p className="text-xs text-[#525252]">The spec will go back to <strong>Draft</strong> with your reason visible to the author.</p>
          <textarea value={reason} onChange={e => setReason(e.target.value)} className="w-full rounded-none border border-[#E5E5E5] px-3 py-2 text-sm" rows={4} placeholder="What needs to change before this can be sent?" data-testid="reject-reason" />
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-none">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="flex-1 rounded-none bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="reject-confirm">
              {saving ? "Rejecting…" : "Reject & return to draft"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SendDialog({ specId, clientId, onClose, onSent, spec }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const isResend = spec?.status === "client_returned";
  const nextVersion = (spec?.current_version || 1) + (isResend ? 1 : 0);

  // Pre-populate with the client's primary or billing contact email, if we
  // know it, so the platform admin doesn't need to type it in most cases.
  // On a resend, default to whoever we sent the previous version to.
  useEffect(() => {
    if (isResend && spec?.sent_to_email) {
      setTo(spec.sent_to_email);
    }
    api.get(`/tenants/${clientId}`).then(r => {
      const c = r.data || {};
      const best = (isResend && spec?.sent_to_email) || c.billing_contact_email || c.pm_contact_email || c.billing_email || c.primary_contact_email || "";
      if (best && !to) setTo(best);
      setContacts([c.billing_contact_email, c.pm_contact_email, c.billing_email, c.primary_contact_email].filter(Boolean));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const submit = async () => {
    if (!to.trim() || !to.includes("@")) return toast.error("Valid recipient email required");
    setSaving(true);
    try { const r = await api.post(`/specs/${specId}/send`, { to_email: to.trim(), message }); onSent(r); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-bold">
            {isResend ? `Send v${nextVersion} — Revised Specification` : "Send Spec to Client"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2" data-testid="send-dialog">
          {isResend && (
            <div className="bg-[#FFF7ED] border border-[#FED7AA] px-3 py-2 text-[11px] text-[#9A3412]">
              <p className="font-bold">Sending revised iteration <strong>v{nextVersion}</strong>.</p>
              <p className="mt-0.5">The client's existing link will now show your updated document. Their previous comment on v{nextVersion - 1} stays on the iteration history.</p>
            </div>
          )}
          <div>
            <label className="text-[10px] font-bold uppercase text-[#525252]">Recipient Email</label>
            <Input value={to} onChange={e => setTo(e.target.value)} className="rounded-none mt-1" data-testid="send-to" />
            {contacts.length > 1 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {contacts.map(c => (
                  <button key={c} onClick={() => setTo(c)} className="text-[10px] text-[#0055FF] hover:underline font-mono" data-testid={`send-preset-${c}`}>{c}</button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-[#525252]">
              {isResend ? "Note to the client (what changed this round)" : "Optional message (prepended to the email)"}
            </label>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              className="w-full rounded-none mt-1 border border-[#E5E5E5] px-3 py-2 text-sm"
              rows={3}
              placeholder={isResend
                ? "e.g. Section 3 revised per your comments. Added clarification on the supplier-based filtering question."
                : "e.g. Following our call today, please find attached the specification we discussed…"}
              data-testid="send-message"
            />
          </div>
          <div className="bg-[#FEF2F2] border border-[#FECACA] px-3 py-2 text-[11px] text-[#991B1B]">
            The client receives a <strong>secure link</strong> — no login, no PDF attachment. The link is unique to them and the page carries a CONFIDENTIAL banner top and bottom. They can Approve or send it back with comments, and every action is recorded on the spec iteration history.
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1 rounded-none">Cancel</Button>
            <Button onClick={submit} disabled={saving} className={`flex-1 rounded-none text-white ${isResend ? "bg-[#D97706] hover:bg-[#B45309]" : "bg-[#1D4ED8] hover:bg-[#1E40AF]"}`} data-testid="send-confirm">
              {saving ? "Sending…" : (isResend ? `Send v${nextVersion}` : "Send secure link")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
